/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { Check, ChevronsUpDown } from "lucide-react";
import { FieldPath, UseFormReturn } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TableCell } from "@/components/ui/table";

import { cn } from "@/lib/utils";

export const TableCellNumberMultiselect = <T extends Record<string, unknown>>({
  fieldName,
  form,
  handleOnSelect,
  index,
  listName,
  numberToLabel,
  options,
}: {
  fieldName: string;
  form: UseFormReturn<T>;
  handleOnSelect: (updatedValue: number, isSelected: boolean) => void;
  index: number;
  listName: "placements" | "requisitions";
  numberToLabel: Record<number, string>;
  options: { value: number; label: string }[];
}) => {
  const fieldNamePath = `${listName}.${index}.${fieldName}` as FieldPath<T>;

  return (
    <TableCell className="p-1">
      <FormField
        control={form.control}
        name={fieldNamePath}
        render={({ field }) => {
          // Type assertion: We know from our generic constraint that the value is E[]
          const value = field.value as number[];

          return (
            <FormItem className="w-full">
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outlineSecondary"
                      // TODO: Clean up, shouldn't be using text-black and clean up more
                      className="w-80 justify-between border-transparent font-normal text-black hover:border-slate-300 hover:bg-transparent hover:text-black focus:border-slate-300 focus:bg-transparent focus:text-black"
                    >
                      <span className="truncate">
                        {value.length > 0
                          ? value
                              .map((value) => numberToLabel[value])
                              .join(", ")
                          : "-"}
                      </span>

                      <ChevronsUpDown className="opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                  <Command>
                    <CommandList>
                      <CommandGroup>
                        {options.map((option) => {
                          const isSelected = value.includes(option.value);
                          return (
                            <CommandItem
                              key={String(option.value)}
                              onSelect={() => {
                                const newValue = isSelected
                                  ? value.filter((v) => v !== option.value)
                                  : [...value, option.value];
                                field.onChange(newValue);
                                handleOnSelect(option.value, isSelected);
                              }}
                            >
                              <Check
                                className={cn(!isSelected && "invisible")}
                              />
                              {option.label}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </FormItem>
          );
        }}
      />
    </TableCell>
  );
};
